<template>
  <div
    ref="wrapperRef"
    class="focus-expand-wrapper"
    :class="{ 'is-active': isActive, 'is-disabled': disabled }"
    :style="wrapperStyles"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
  >
    <slot />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  },
  autoScale: {
    type: Boolean,
    default: true
  },
  customScale: {
    type: Number,
    default: null
  },
  minScale: {
    type: Number,
    default: 1.05
  },
  maxScale: {
    type: Number,
    default: 1.65
  }
})

const wrapperRef = ref(null)
const isActive = ref(false)
const focusScale = ref(props.customScale || 1.5)
const baseWidth = ref(0)
const expandedWidth = ref(0)
const expandedMargin = ref(0)
let resizeObserver

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const determineScale = (width) => {
  if (!width) return focusScale.value

  if (!props.autoScale && props.customScale) {
    return props.customScale
  }

  let newScale
  if (width <= 120) newScale = 1.65
  else if (width <= 180) newScale = 1.45
  else if (width <= 240) newScale = 1.3
  else if (width <= 320) newScale = 1.2
  else newScale = 1.1

  return clamp(newScale, props.minScale, props.maxScale)
}

const handleFocusIn = () => {
  if (props.disabled) return

  if (wrapperRef.value) {
    baseWidth.value = wrapperRef.value.offsetWidth || 0
    const scale = determineScale(baseWidth.value)
    focusScale.value = scale

    if (baseWidth.value) {
      expandedWidth.value = baseWidth.value * scale
      expandedMargin.value = (expandedWidth.value - baseWidth.value) / 2
    }
  }

  isActive.value = true
}

const handleFocusOut = () => {
  isActive.value = false
  expandedWidth.value = 0
  expandedMargin.value = 0
}

const wrapperStyles = computed(() => {
  if (isActive.value && expandedWidth.value) {
    return {
      width: `${expandedWidth.value}px`,
      marginLeft: `-${expandedMargin.value}px`,
      marginRight: `-${expandedMargin.value}px`
    }
  }

  return {
    width: '100%',
    marginLeft: '0',
    marginRight: '0'
  }
})

const observeWidth = () => {
  if (!wrapperRef.value || typeof ResizeObserver === 'undefined') {
    return
  }

  const updateScale = (width) => {
    if (isActive.value) return
    if (!width) return

    const newScale = determineScale(width)
    if (Math.abs(newScale - focusScale.value) > 0.05) {
      focusScale.value = newScale
    }
  }

  resizeObserver = new ResizeObserver((entries) => {
    if (!entries || entries.length === 0) return
    const { contentRect } = entries[0]
    updateScale(contentRect.width)
  })

  resizeObserver.observe(wrapperRef.value)
  updateScale(wrapperRef.value.offsetWidth)
}

onMounted(() => {
  observeWidth()
})

onBeforeUnmount(() => {
  if (resizeObserver && wrapperRef.value) {
    resizeObserver.unobserve(wrapperRef.value)
  }
  resizeObserver = null
})
</script>

<style scoped>
.focus-expand-wrapper {
  position: relative;
  display: inline-flex;
  width: 100%;
  transition: width 0.2s ease, margin 0.2s ease, box-shadow 0.2s ease;
  border-radius: 0.35rem;
}

.focus-expand-wrapper.is-active {
  box-shadow: 0 8px 20px rgba(0, 86, 179, 0.18);
  z-index: 5;
}

.focus-expand-wrapper.is-disabled {
  box-shadow: none;
}
</style>

